/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlassian/tangerine/import/no-parent-imports */

import { useCallback, useEffect } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { css, cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CopyIcon from '@atlaskit/icon/core/copy';
import Image from '@atlaskit/image';
import { CloseButton } from '@atlaskit/modal-dialog';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import ModalBackgroundDark from '../../assets/confirmationBackgroundV2.dark.png';
import ModalBackgroundLight from '../../assets/confirmationBackgroundV2.light.png';
import VisualDark from '../../assets/confirmationVisual.dark.svg';
import VisualLight from '../../assets/confirmationVisual.light.svg';
import TeamworkCollectionLogoDark from '../../assets/teamworkCollectionHeaderV2.dark.png';
import TeamworkCollectionLogoLight from '../../assets/teamworkCollectionHeaderV2.light.png';
import { ScreenLayout } from '../../components/screen-layout';
import { useModalContext } from '../../contexts/modal-context';
import { useCopy } from '../../hooks/useCopy';
import { useIsDarkMode } from '../../hooks/useIsDarkMode';

import { messages } from './messages';

const styles = cssMap({
	content: {
		display: 'flex',
		height: '100%',
		flexDirection: 'column',
		paddingTop: token('space.300'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		'@media (min-width: 64rem)': {
			paddingTop: token('space.600'),
			paddingLeft: token('space.600'),
			paddingRight: token('space.600'),
			paddingBottom: token('space.600'),
		},
	},
	closeButton: {
		position: 'absolute',
		top: token('space.500'),
		right: token('space.500'),
		'@media (min-width: 64rem)': {
			top: token('space.200'),
			right: token('space.200'),
		},
	},
	modalContent: {
		display: 'flex',
		flexGrow: 1,
		flexDirection: 'row',
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.large'),
	},
	header: {
		display: 'flex',
		flexDirection: 'column',
	},
	heading: {
		marginTop: token('space.300'),
	},
	shareLinkSection: {
		display: 'flex',
		flexDirection: 'column',
		marginTop: token('space.300'),
		gap: token('space.100'),
	},
	linkInputContainer: {
		display: 'flex',
		gap: token('space.100'),
		alignItems: 'center',
	},
	textfieldWrapper: {
		flex: 1,
	},
	copyButton: {
		flexShrink: 0,
	},
	actions: {
		display: 'flex',
		justifyContent: 'flex-end',
		marginTop: 'auto',
		paddingTop: token('space.400'),
	},
	successMessage: {
		marginTop: token('space.050'),
	},
	leftColumn: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		paddingTop: token('space.500'),
		paddingLeft: token('space.500'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.500'),
	},
	rightColumn: {
		display: 'none',
		'@media (min-width: 64rem)': {
			flexShrink: 0,
			display: 'flex',
			paddingTop: token('space.400'),
			paddingLeft: token('space.400'),
			paddingRight: token('space.400'),
			paddingBottom: token('space.400'),
		},
	},
	subheading: {
		marginTop: token('space.050'),
		font: token('font.body.small'),
	},
});

const textfieldStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	'& > [data-ds--text-field--input]': {
		// input style to match button height (30px)
		height: '26px',
	} as any,
});

type Props = {
	approvalUrl: string | undefined;
	onClose: () => void;
};

export const RequestedTrialScreen = ({ onClose, approvalUrl }: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isDarkMode = useIsDarkMode();
	const { sourceComponent, sourceContext } = useModalContext();

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcXflowModalRequestedTrialScreen',
			attributes: {
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');
	}, [createAnalyticsEvent, sourceComponent, sourceContext]);

	const { copy: handleCopyLink, copied: linkCopied } = useCopy(approvalUrl ?? '', {
		onCopy: () => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'twcXflowModalRequestedTrialScreenCopyLink',
				attributes: {
					sourceComponent,
					sourceContext,
				},
			}).fire('growth');
		},
	});

	const handleClose = useCallback(() => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcXflowModalRequestedTrialScreenClose',
			attributes: {
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');
		onClose();
	}, [onClose, createAnalyticsEvent, sourceComponent, sourceContext]);

	return (
		<ScreenLayout
			testId="requested-trial-screen"
			ModalBackgroundDark={ModalBackgroundDark}
			ModalBackgroundLight={ModalBackgroundLight}
		>
			<Box xcss={styles.content}>
				<Box xcss={styles.closeButton}>
					<CloseButton onClick={onClose} label={formatMessage(messages.closeModal)} />
				</Box>
				<Box xcss={styles.modalContent}>
					<Box xcss={styles.leftColumn}>
						<Box xcss={styles.header}>
							<Image
								src={isDarkMode ? TeamworkCollectionLogoDark : TeamworkCollectionLogoLight}
								srcDark={TeamworkCollectionLogoDark}
								alt={formatMessage(messages.teamworkCollectionAlt)}
								testId="teamwork-collection-header"
								width="134"
								height="32"
							/>

							<Box xcss={styles.heading}>
								<Heading size="medium" testId="requested-trial-modal-heading">
									<FormattedMessage {...messages.heading} />
								</Heading>
							</Box>

							<Box xcss={styles.subheading}>
								<Text
									size="small"
									color="color.text.subtle"
									testId="requested-trial-modal-subheading"
								>
									<FormattedMessage {...messages.explanation} />
								</Text>
							</Box>

							{approvalUrl && (
								<Box xcss={styles.shareLinkSection}>
									<Text size="small" color="inherit" weight="bold">
										<FormattedMessage {...messages.shareLinkPrompt} />
									</Text>

									<Box xcss={styles.linkInputContainer}>
										<Box xcss={styles.textfieldWrapper}>
											<Textfield
												value={approvalUrl || ''}
												readOnly
												// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides -- Required to match button height
												// @ts-ignore - css prop not in Textfield types but works at runtime
												css={textfieldStyles}
												onChange={() => {
													// No-op handler for read-only field to satisfy React warning
												}}
												testId="request-link-input"
												aria-label={formatMessage(messages.requestLinkInputLabel)}
												id="request-link-input"
											/>
										</Box>
										<Box xcss={styles.copyButton}>
											<Button
												appearance="default"
												iconBefore={CopyIcon as any}
												onClick={handleCopyLink}
												testId="copy-link-button"
											>
												<FormattedMessage {...messages.copyLinkButton} />
											</Button>
										</Box>
									</Box>

									{linkCopied && (
										<Box xcss={styles.successMessage}>
											<Text size="small" color="color.text.success">
												<FormattedMessage {...messages.linkCopied} />
											</Text>
										</Box>
									)}
								</Box>
							)}
						</Box>

						<Box xcss={styles.actions}>
							<Button appearance="primary" onClick={handleClose} testId="close-button">
								<FormattedMessage {...messages.closeButton} />
							</Button>
						</Box>
					</Box>

					<Box xcss={styles.rightColumn}>
						<Image
							src={isDarkMode ? VisualDark : VisualLight}
							srcDark={VisualDark}
							alt={formatMessage(messages.visualAlt)}
							testId="teamwork-collection-visual"
							width="345"
							height="400"
						/>
					</Box>
				</Box>
			</Box>
		</ScreenLayout>
	);
};
